import {
    createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Sparkle, X, PawPrint } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { track } from "@/lib/analytics";

const easeOut = [0.16, 1, 0.3, 1];

/**
 * PremiumComingSoonModal — small, elegant, positive.
 *
 * Rendered globally via <PremiumComingSoonProvider>; any component can open
 * it by calling `useShowPremiumComingSoon()(featureLabel?)`.
 *
 * Contract (from product direction 2026-02-25):
 *   • No pricing.
 *   • No purchase button.
 *   • No external link.
 *   • No fake urgency or countdown.
 *   • Positive, reassuring tone — never "error" language.
 *   • Single "Got it" dismiss.
 *
 * Fires GA4 events:
 *   • `premium_gate_shown` with { feature }
 *   • `premium_gate_dismissed` with { feature }
 * (No new SDK; uses the existing `lib/analytics.js` pipeline.)
 */

const PremiumComingSoonContext = createContext({ show: (_feature) => {} });

export function useShowPremiumComingSoon() {
    return useContext(PremiumComingSoonContext).show;
}

export function PremiumComingSoonProvider({ children }) {
    const [feature, setFeature] = useState(null);   // null = closed
    const { t } = useI18n();
    const reduce = useReducedMotion();

    const show = useCallback((f = "premium") => {
        setFeature(f || "premium");
        track("premium_gate_shown", { feature: f || "premium" });
    }, []);

    const close = useCallback(() => {
        if (feature) track("premium_gate_dismissed", { feature });
        setFeature(null);
    }, [feature]);

    const value = useMemo(() => ({ show }), [show]);

    // Close on Escape.
    useEffect(() => {
        if (!feature) return;
        const onKey = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [feature, close]);

    return (
        <PremiumComingSoonContext.Provider value={value}>
            {children}
            <AnimatePresence>
                {feature && (
                    <motion.div
                        key="prem-soon-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: reduce ? 0 : 0.2 }}
                        onClick={close}
                        data-testid="premium-coming-soon-backdrop"
                        className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm grid place-items-center px-4"
                    >
                        <motion.div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="prem-soon-title"
                            data-testid="premium-coming-soon-modal"
                            data-feature={feature}
                            initial={{ opacity: 0, y: 12, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.98 }}
                            transition={{ duration: reduce ? 0 : 0.35, ease: easeOut }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-sm rounded-3xl border border-paw-terra/25 bg-white dark:bg-[hsl(152,15%,10%)] px-6 py-6 shadow-2xl"
                        >
                            <button
                                type="button"
                                onClick={close}
                                aria-label="Close"
                                data-testid="premium-coming-soon-x"
                                className="absolute top-3 end-3 w-8 h-8 rounded-full grid place-items-center text-paw-ink2/60 hover:text-paw-ink dark:text-paw-bone/50 dark:hover:text-paw-bone hover:bg-paw-ink/5 dark:hover:bg-paw-bone/5 transition"
                            >
                                <X size={14} weight="bold" />
                            </button>

                            <div className="flex items-center gap-2 mb-3">
                                <span className="w-11 h-11 rounded-2xl bg-paw-terra/15 ring-1 ring-paw-terra/25 grid place-items-center">
                                    <Sparkle size={20} weight="fill" className="text-paw-terra" />
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-paw-terra text-white text-[10px] font-bold uppercase tracking-[0.22em] px-2.5 py-1">
                                    <PawPrint size={10} weight="fill" />
                                    {t("premium_soon.badge")}
                                </span>
                            </div>

                            <h3
                                id="prem-soon-title"
                                data-testid="premium-coming-soon-title"
                                className="text-lg font-semibold text-paw-ink dark:text-paw-bone leading-snug"
                            >
                                {t("premium_soon.title")}
                            </h3>
                            <p className="mt-2 text-sm text-paw-ink2 dark:text-paw-bone/70 leading-relaxed">
                                {t("premium_soon.body")}
                            </p>
                            <p className="mt-2 text-[13px] text-paw-ink2 dark:text-paw-bone/55 leading-relaxed">
                                {t("premium_soon.free_hint")}
                            </p>

                            <button
                                type="button"
                                onClick={close}
                                data-testid="premium-coming-soon-ok"
                                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 py-3 text-sm font-semibold transition-all duration-300 active:scale-[0.98]"
                            >
                                {t("premium_soon.ok")}
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </PremiumComingSoonContext.Provider>
    );
}
